import { MODULES } from '../data/modules';
import { boss, enemies, ETYPE } from '../entities/enemies';
import { MECHS } from '../entities/mechs';
import { $ } from '../platform/dom';
import { META, saveMeta } from '../platform/storage';
import { killEnemy, STATE, heal } from '../sim/fx';
import { impactRatio } from '../sim/impact';
import { lockedTarget } from '../sim/lockon';
import { RUN_LEN, startBoss } from '../sim/stage-flow';
import { spawnEnemy } from '../sim/spawn';
import { runWave } from '../sim/waves';
import { CAM, PRESETS, camPresetList, camReadout, setCamPreset } from '../sim/camera';
import { WAVES } from '../data/waves';
import { ELITE_MAX_ALIVE, spawnEliteSquad } from '../sim/elites';
import { dropChest } from '../sim/fx';
import { equipMech, P } from '../world/player';
import { RUN_CFG } from './screens/brief';
import { maxAllParts, resetPartsToBaseline } from '../sim/meta-parts';

import { STAGES } from '../world/arena';
import { ROSTER_IDS } from '../data/boss-roster';
import { RS } from '../sim/spawn';
import { enemyPaletteModes, setEnemyPalette } from '../entities/enemy-parts';
import { enemyField } from '../render/enemy-field';
import { updateSlotsUI as updateSlots } from './levelup';
import { toggleFly } from '../render/flycam';
import { scene } from '../render/scene';

/* ============================================================
   除錯主控台 —— 反引號 ` 開關

   目的很具體：測 Boss 與換武裝時，不想每次都從第一波打 40 秒。
   所有指令都只是呼叫既有的遊戲函式，沒有任何一條是「只有除錯模式才有的邏輯」，
   所以它不會讓正式流程與測試流程分岔。

   刻意不做的事：不攔截遊戲的按鍵（只在輸入框有焦點時吃鍵），
   不暫停模擬（要看得到指令的即時效果）。
   ============================================================ */

type Cmd = { args: string; help: string; run: (a: string[]) => string };

/** `fog off` 之後要記得原本那顆，不然開回來時整個場地沒有大氣透視。 */
var savedFog: any = null;

var CMDS: Record<string, Cmd> = {
  boss: {
    args: '[id|序號]', help: '召喚 Boss：' + ROSTER_IDS.join(' / ') + '（省略＝本幕的那一隻）',
    run: function (a) {
      /* B2 起五隻各自獨立，所以要指名 —— 截圖驗收要一隻一隻拍，
         而「本階段的那一隻」在最終 Boss（第 5 隻，沒有對應的幕）上叫不出來。 */
      var i = a[0] === undefined ? RS.act : (isFinite(+a[0]) ? +a[0] : ROSTER_IDS.indexOf(a[0]));
      if (i < 0 || i >= ROSTER_IDS.length) return '可用：' + ROSTER_IDS.join(' / ');
      startBoss(i);
      return '召喚 ' + ETYPE[ROSTER_IDS[i]].bn;
    }
  },
  /* 階段推進在 M7 退場，改成直接跳時鐘 —— 時鐘現在是唯一的進度。
     換幕與 Boss 降臨都掛在時鐘上，所以跳時間等於跳到那一幕。 */
  time: {
    args: '<分鐘>', help: '把時鐘跳到指定分鐘（換幕與 Boss 都吃時鐘）',
    run: function (a) {
      var t = parseFloat(a[0]);
      if (!isFinite(t) || t < 0) return '用法：time <分鐘>';
      STATE.t = Math.min(RUN_LEN - 1, t * 60);
      return '時鐘跳到 ' + (STATE.t / 60).toFixed(1) + ' 分';
    }
  },
  mech: {
    args: '<id>', help: '換機體：' + Object.keys(MECHS).join(' / '),
    run: function (a) {
      if (!MECHS[a[0]]) return '可用：' + Object.keys(MECHS).join(' / ');
      /* `RUN_CFG.mech` 是「出擊要開哪台」的單一事實來源（`ui/screens/brief.ts`）。
         只 equipMech 的話下一次出擊／重玩同種子會換回別台 —— 同型的 bug
         在機庫那邊發生過一次（使用者 2026-08-06 回報）。 */
      equipMech(a[0]); RUN_CFG.mech = a[0]; updateSlots();
      return '換上 ' + MECHS[a[0]].cn + '（主武裝 ' + MECHS[a[0]].primary + '）';
    }
  },
  mod: {
    args: '<id|clear>', help: '裝上行為模組。不帶參數列出目前機體可用的',
    run: function (a) {
      var mech = P.mech;
      var usable = MODULES.filter(function (m) {
        return m.w === mech.primary || m.w === mech.mid || m.w === mech.ult;
      });
      if (a[0] === 'clear') { P.mod = { primary: null, mid: null, ult: null }; updateSlots(); return '已卸下全部模組'; }
      if (!a[0]) return usable.map(function (m) { return m.id + '（' + m.n + '）'; }).join('  ') || '無';
      var hit = usable.filter(function (m) { return m.id === a[0]; })[0];
      if (!hit) return '這台機體沒有 ' + a[0] + '。可用：' + usable.map(function (m) { return m.id; }).join(' ');
      P.mod[hit.slot] = hit.id; updateSlots();
      return '裝上 ' + hit.n + '（' + hit.slot + '）';
    }
  },
  spawn: {
    args: '<type> [n]', help: '生成敵人：' + Object.keys(ETYPE).join(' / '),
    run: function (a) {
      if (!ETYPE[a[0]]) return '可用：' + Object.keys(ETYPE).join(' / ');
      var n = Math.min(30, parseInt(a[1], 10) || 1);
      for (var i = 0; i < n; i++) {
        var ang = (i / n) * Math.PI * 2;
        spawnEnemy(a[0], P.pos.x + Math.cos(ang) * 14, P.pos.z + Math.sin(ang) * 14);
      }
      return '生成 ' + n + ' 隻 ' + a[0];
    }
  },
  /* 波次要等 40 秒才來一次，而且抽到哪一種是隨機的 —— 光靠玩驗不了七種劇本。
     `wave` 直接放一整份（含所有梯次），方位可以指定，這樣才比對得出
     「預告的方向」與「真的來的方向」是不是同一邊。 */
  wave: {
    args: '<id> [方位角度]', help: '立刻放一整波：' + WAVES.map(function (w) { return w.id; }).join(' / '),
    run: function (a) {
      if (!a[0]) return '可用：' + WAVES.map(function (w) { return w.id + '（' + w.n + '）'; }).join('  ');
      var deg = parseFloat(a[1]);
      var n = runWave(a[0], isFinite(deg) ? deg * Math.PI / 180 : undefined, true);
      return n ? '放出 ' + n : '沒有這種波次：' + a[0];
    }
  },
  elite: {
    args: '[n]', help: '立刻降下一組精英（不指定就照當下時間的組員數）',
    run: function (a) {
      var want = parseInt(a[0], 10);
      var t = isFinite(want) && want > 0
        ? (want >= 4 ? 960 : want === 3 ? 660 : want === 2 ? 300 : 0)   // 反推一個會給出這麼多隻的時間
        : STATE.t;
      var got = spawnEliteSquad(t, undefined, true);
      return got ? '降下 ' + got + ' 隻精英（場上上限 ' + ELITE_MAX_ALIVE + '）' : '場上精英已達上限';
    }
  },
  /* 鏡頭是品味判斷（`judgment.md §6`）—— 唯一有意義的驗收是「自己開著看」。
     所以預設做成可以即時切換的表，而不是每次改常數重 build。 */
  /* 自由視角。**看地圖用的東西要用地圖的方式看** —— 版面、密度、
     天際線的節奏、量體之間走不走得過去，這些在跟隨鏡頭裡都只看得到一小塊。 */
  fly: {
    args: '[on|off]', help: '上帝視角：像 3D 編輯器一樣飛（WASD／QE／拖曳滑鼠／Shift 加速／滾輪調速）',
    run: function (a) {
      var f = a[0] === undefined ? undefined : (a[0] === 'on' || a[0] === '1');
      return toggleFly(f);
    }
  },
  fog: {
    args: '[on|off|密度]', help: '霧的開關與濃度（飛高看地圖時關掉它，否則遠處全是霧色）',
    run: function (a) {
      if (!a[0]) return '霧：' + (scene.fog ? '開（密度 ' + (scene.fog as any).density.toFixed(4) + '）' : '關');
      if (a[0] === 'off' || a[0] === '0') { if (scene.fog) { savedFog = scene.fog; scene.fog = null; } return '霧 關'; }
      if (a[0] === 'on') { if (!scene.fog && savedFog) scene.fog = savedFog; return '霧 開'; }
      var d = parseFloat(a[0]);
      if (!isFinite(d) || d < 0) return '用法：fog on | off | <密度，例 0.002>';
      if (!scene.fog && savedFog) scene.fog = savedFog;
      if (scene.fog) (scene.fog as any).density = d;
      return '霧密度 → ' + d;
    }
  },
  cam: {
    args: '<預設 id | dyn | zoom n>', help: '切戰場鏡頭：' + Object.keys(PRESETS).join(' / ') + '；dyn 開關動態',
    run: function (a) {
      if (a[0] === 'dyn') { CAM.dynamics = !CAM.dynamics; return '鏡頭動態 ' + (CAM.dynamics ? '開' : '關'); }
      /* 滾輪調完之後要報得出數字，所以無參數印的是**實際值**不是預設 id。 */
      if (!a[0]) return camReadout() + '\n可用：' + camPresetList() + '（滾輪縮放）';
      if (a[0] === 'zoom') {
        var z = parseFloat(a[1]);
        if (isFinite(z)) CAM.userZoom = Math.max(0.55, Math.min(1.9, z));
        return camReadout();
      }
      var n = setCamPreset(a[0]);
      return n ? '鏡頭 → ' + a[0] + '（' + n + '）' : '沒有這個預設。可用：' + camPresetList();
    }
  },
  /* 色彩處理是**品味判斷**，只能並排比（`judgment.md §6`）。而它在圖鑑檢視台
     裡比出來的結論不一定適用於同屏 400 隻的戰場 —— 那正是這個指令存在的理由。
     切換之後一定要清掉 Field：材質是型別建立時抓的，不清的話只有新型別會換色。 */
  pal: {
    args: '<' + enemyPaletteModes().join(' | ') + '>', help: '切敵人色彩處理（並排比較用）',
    run: function (a) {
      var m = setEnemyPalette(a[0]);
      if (!m) return '可用：' + enemyPaletteModes().join(' / ');
      enemyField().clear();
      return '敵人色盤 → ' + m + '（已重建型別）';
    }
  },
  scrap: {
    args: '[n]', help: '給廢料（預設 5000）。局外部件要用它買',
    run: function (a) {
      META.scrap = (META.scrap || 0) + (parseInt(a[0], 10) || 5000);
      saveMeta();
      return '廢料 → ' + META.scrap;
    }
  },
  unlockall: {
    args: '', help: '全部局外部件買滿並升到 5 級（試玩用）',
    run: function () {
      maxAllParts(); saveMeta();
      equipMech(P.mech ? P.mech.id : 'chassis0');
      return '18 個部件全部解鎖並升滿。裝備仍是各部位的起手件，去機庫換。';
    }
  },
  /* `unlockall` 的反向。2026-08-06 加的，起因是使用者實玩覺得「前期壓力太低」，
     而他的部件是全滿的 —— 實測全滿對零部件是 **DPS ×1.64、結構 ×1.56、
     外加開局免費 4 級**（遠超 GDD §11.4 的「永久強化不超過 30%」，
     那是交接清單第 4 項的待辦）。

     ⚠️ **量測 bot 跑的是零部件**（`metrics.test.ts` 的 `resetPartsToBaseline()`），
     所以「實玩的手感」與「報表的難度」是兩個不同的遊戲。
     調難度曲線之前先用這個指令切到同一個基準，不然會照著滿部件的體感
     去補償，而那個補償會直接殺死新玩家。 */
  parts0: {
    args: '', help: '局外部件全部歸零（＝新玩家的狀態，也是量測 bot 跑的基準）',
    run: function () {
      resetPartsToBaseline(); saveMeta();
      equipMech(P.mech ? P.mech.id : 'chassis0');
      return '局外部件已歸零（廢料也歸零）。這是量測 bot 的基準線 —— '
        + '要調難度曲線就用這個狀態試玩。';
    }
  },
  chest: {
    args: '[n] [mw]', help: '在腳邊掉 n 個寶箱（加 mw ＝ 橘色的武裝強化箱，精英小機率掉的那種）',
    run: function (a) {
      var mw = a.indexOf('mw') >= 0;
      var n = Math.min(5, parseInt(a[0], 10) || 1);
      for (var i = 0; i < n; i++) {
        var ang = (i / n) * Math.PI * 2;
        dropChest(P.pos.x + Math.cos(ang) * 3.5, P.pos.z + Math.sin(ang) * 3.5, mw);
      }
      return '掉了 ' + n + ' 個' + (mw ? '武裝強化箱' : '寶箱');
    }
  },
  kill: {
    args: '', help: '清除場上所有敵人（Boss 除外，要打 Boss 用 killboss）',
    run: function () {
      var n = 0;
      for (var i = enemies.length - 1; i >= 0; i--) if (enemies[i] && !enemies[i].T.boss) { killEnemy(enemies[i]); n++; }
      return '清除 ' + n + ' 隻';
    }
  },
  bosshp: {
    args: '<比例>', help: '把 Boss 的血壓到指定比例（看階段變形用，0–1）',
    run: function (a) {
      if (!boss || !boss.alive) return '場上沒有 Boss';
      var k = parseFloat(a[0]);
      if (!isFinite(k) || k <= 0 || k > 1) return '用法：bosshp 0.45';
      /* 直接寫 `hp`，讓下一個模擬步的門檻檢查自己觸發階段推進 ——
         在這裡直接叫 `enterPhase` 的話，驗收看到的就不是遊戲真正的路徑。 */
      boss.hp = boss.hpMax * k;
      return (boss.T.bn) + ' → ' + Math.round(k * 100) + '%';
    }
  },
  killboss: {
    args: '', help: '秒殺 Boss',
    run: function () { if (!boss || !boss.alive) return '場上沒有 Boss'; killEnemy(boss); return 'Boss 已擊破'; }
  },
  stagger: {
    args: '', help: '讓鎖定目標（或 Boss）立刻踉蹌，用來看增傷窗口',
    run: function () {
      var t = lockedTarget() || boss;
      if (!t || !t.alive) return '沒有鎖定目標也沒有 Boss';
      if (!t.T.imp) return (t.T.cn || t.type) + ' 不會踉蹌';
      t.impact = t.T.imp; t.staggerT = t.T.boss ? 2.6 : 1.6; t.dirty = true;
      return (t.T.bn || t.T.cn) + ' 進入踉蹌';
    }
  },
  god: {
    args: '', help: '切換無敵',
    run: function () { P.god = !P.god; return P.god ? '無敵：開' : '無敵：關'; }
  },
  thrust: {
    args: '[%]', help: '設定推進能量（百分比，不帶參數則補滿）。驗收飛行用',
    run: function (a) {
      var pc = a[0] ? Math.max(0, Math.min(100, parseFloat(a[0]))) : 100;
      P.thrust = (P.s.thrustMax || 100) * pc / 100; P.thrustCd = 0;
      return '推進能量 ' + Math.round(P.thrust) + ' / ' + Math.round(P.s.thrustMax || 0);
    }
  },
  hp: {
    args: '[n]', help: '設定結構值，不帶參數則補滿',
    run: function (a) {
      P.hp = a[0] ? Math.min(P.hpMax, parseFloat(a[0])) : P.hpMax;
      if (!a[0]) { P.sh = P.shMax; heal(0); }
      return '結構 ' + Math.round(P.hp) + ' / ' + P.hpMax;
    }
  },
  unlock: {
    args: '', help: '解鎖全部機體與高階模組',
    run: function () {
      META.tier2 = true; META.mechs = Object.keys(MECHS);
      return '已解鎖：' + META.mechs.join(' / ') + '、高階模組';
    }
  },
  info: {
    args: '', help: '印出目前狀態',
    run: function () {
      var t = lockedTarget();
      return [
        '機體 ' + P.mech.cn + '　結構 ' + Math.round(P.hp) + '/' + P.hpMax + '　護盾 ' + Math.round(P.sh),
        '階段 ' + (STATE.stage + 1) + '　phase ' + STATE.phase + '　場上 ' + enemies.length + ' 隻',
        '模組 ' + JSON.stringify(P.mod),
        '鎖定 ' + (t ? (t.T.bn || t.T.cn) + '　衝擊 ' + (impactRatio(t) * 100).toFixed(0) + '%' : '無'),
      ].join('\n');
    }
  },
};

var el: HTMLElement | null = null, out: HTMLElement | null = null, inp: HTMLInputElement | null = null;
var hist: string[] = [], histIdx = -1;

function print(s: string, cls?: string) {
  if (!out) return;
  var d = document.createElement('div');
  if (cls) d.className = cls;
  d.textContent = s;
  out.appendChild(d);
  out.scrollTop = out.scrollHeight;
}

function exec(line: string) {
  var parts = line.trim().split(/\s+/);
  var name = parts[0].toLowerCase();
  if (!name) return;
  print('> ' + line, 'echo');
  if (name === 'help' || name === '?') {
    for (var k in CMDS) print(('  ' + k + ' ' + CMDS[k].args).padEnd(22) + CMDS[k].help);
    return;
  }
  var c = CMDS[name];
  if (!c) { print('沒有這個指令：' + name + '（輸入 help）', 'err'); return; }
  try { print(c.run(parts.slice(1))); }
  catch (e: any) { print('執行失敗：' + (e && e.message), 'err'); }
}

export function initConsole() {
  el = document.createElement('div');
  el.id = 'dbgConsole';
  el.innerHTML = '<div id="dbgOut"></div><input id="dbgIn" autocomplete="off" spellcheck="false" placeholder="輸入 help 看指令">';
  document.body.appendChild(el);
  out = $('dbgOut'); inp = $('dbgIn') as HTMLInputElement;

  print('除錯主控台　`（反引號）開關　輸入 help 看指令', 'echo');

  window.addEventListener('keydown', function (e) {
    if (e.key === '`' || e.key === '~') {
      e.preventDefault();
      var open = el!.classList.toggle('show');
      if (open) inp!.focus(); else inp!.blur();
      return;
    }
    if (!el!.classList.contains('show') || e.target !== inp) return;
    if (e.key === 'Enter') {
      var v = inp!.value; inp!.value = '';
      if (v.trim()) { hist.push(v); histIdx = hist.length; exec(v); }
    } else if (e.key === 'ArrowUp' && histIdx > 0) {
      histIdx--; inp!.value = hist[histIdx];
    } else if (e.key === 'ArrowDown') {
      histIdx = Math.min(hist.length, histIdx + 1);
      inp!.value = histIdx >= hist.length ? '' : hist[histIdx];
    }
    e.stopPropagation();
  }, true);
}
